import { DateTime } from 'luxon';
import { InitialSyncSettledSubscribe, runOnInitialSyncSuccess } from './user-db.task-status';

/**
 * The past days whose backfill the backlog rebuild kept in memory because this session's sync had
 * not succeeded yet, and the one rebuild that saves them once it has (#6280).
 *
 * A device back after days away rebuilds its missed days within a second of boot, from its own
 * copy of the schedule. Saving that rebuild before the sync has delivered the other device's newer
 * schedule and entries wrote conflicting revisions and stray meals. So the rebuild holds those
 * entries (`holdNewPastEntries` in `user-db.backlog-save.ts`) and tells this class which days it
 * held. When the sync then succeeds, this asks for those days to be rebuilt once more, which now
 * saves them from the schedule the sync confirmed.
 *
 * `UserDb` keeps a second instance for the home page's re-save of today (#6828): there the held
 * day is re-saved, not rebuilt, once the sync has succeeded. Nothing here is specific to past days.
 *
 * Free of Angular and of the database, so it can be unit tested with a fake subscription. See
 * design-docs/sync-integrity.md, "A returning device holds its past-day backfill until the sync
 * succeeds (#6280)".
 */
export class PastBackfillHold {
  /** the held days, as ISO dates in the device's zone */
  private readonly heldDays = new Set<string>();
  private releaseArmed = false;
  private stopRelease: (() => void) | null = null;

  /**
   * @param subscribe registers for every initial-sync outcome of the user database
   * @param rebuildAsync rebuilds and saves the days from `first` to `last`, both start of day
   * @param onRebuildFailed told when that rebuild throws; the days are not held again
   */
  constructor(
    private readonly subscribe: InitialSyncSettledSubscribe,
    private readonly rebuildAsync: (first: DateTime, last: DateTime) => Promise<void>,
    private readonly onRebuildFailed: (err: unknown) => void,
  ) {}

  /** Record days whose new entries were kept in memory, and wait for the sync to succeed. */
  hold(days: Iterable<string>): void {
    for (const day of days) {
      this.heldDays.add(day);
    }
    if (this.heldDays.size === 0 || this.releaseArmed) return;
    this.releaseArmed = true;
    const stop = runOnInitialSyncSuccess(this.subscribe, () => {
      this.releaseArmed = false;
      this.stopRelease = null;
      this.release();
    });
    // the success can only arrive on a later microtask today, but if it ever came synchronously
    // the callback above has already run, and storing its spent handle would look like a live one
    if (this.releaseArmed) {
      this.stopRelease = stop;
    }
  }

  /** Forget the held days and stop waiting: the database these days belong to is going away. */
  stop(): void {
    this.stopRelease?.();
    this.stopRelease = null;
    this.releaseArmed = false;
    this.heldDays.clear();
  }

  private release(): void {
    const days = [...this.heldDays].sort();
    this.heldDays.clear();
    if (days.length === 0) return;
    const first = DateTime.fromISO(days[0]).startOf('day');
    const last = DateTime.fromISO(days[days.length - 1]).startOf('day');
    this.rebuildAsync(first, last).catch(err => this.onRebuildFailed(err));
  }
}
